import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Award } from 'lucide-react';
import QRCode from 'qrcode';

const CertificatePrint = () => {
  const { certificateId } = useParams();
  const [searchParams] = useSearchParams();
  const isPrint = searchParams.get('print') === 'true';
  const [certificate, setCertificate] = useState(null);
  const [landingConfig, setLandingConfig] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch certificate and landing config in parallel
        const [certResponse, configResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verify/certificate/${certificateId}`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landing/config`)
        ]);
        
        const certData = await certResponse.json();
        const configData = await configResponse.json();
        
        setLandingConfig(configData);
        
        if (certData.valid) {
          setCertificate(certData.certificate);
          
          // Generate QR code
          const verifyUrl = `${window.location.origin}/verify/certificate/${certificateId}`;
          const qr = await QRCode.toDataURL(verifyUrl, { width: 120, margin: 1 });
          setQrCodeUrl(qr);
        } else {
          setError(certData.message || 'Certificate not found');
        }
      } catch (err) {
        setError('Failed to load certificate');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [certificateId]);

  // Track when all images are loaded for Playwright
  useEffect(() => {
    if (!loading && certificate && contentRef.current) {
      const images = contentRef.current.querySelectorAll('img');
      if (images.length === 0) {
        setImagesLoaded(true);
        return;
      }
      
      let loadedCount = 0;
      const checkAllLoaded = () => {
        loadedCount++;
        if (loadedCount >= images.length) {
          setImagesLoaded(true);
        }
      };
      
      images.forEach(img => {
        if (img.complete) {
          checkAllLoaded();
        } else {
          img.addEventListener('load', checkAllLoaded);
          img.addEventListener('error', checkAllLoaded);
        }
      });
      
      // Fallback timeout to mark as ready after 5 seconds
      const timeout = setTimeout(() => setImagesLoaded(true), 5000);
      return () => clearTimeout(timeout);
    }
  }, [loading, certificate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-red-500">{error || 'Certificate not found'}</p>
      </div>
    );
  }

  const tierConfig = {
    gold: { bg: 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500', emoji: '🥇', text: 'GOLD' },
    silver: { bg: 'bg-gradient-to-r from-gray-300 via-gray-400 to-gray-500', emoji: '🥈', text: 'SILVER' },
    bronze: { bg: 'bg-gradient-to-r from-orange-400 via-orange-500 to-amber-600', emoji: '🥉', text: 'BRONZE' }
  };

  const tier = tierConfig[certificate.tier] || tierConfig.bronze;
  const academyName = landingConfig?.hero?.academy_name || certificate.academy_name || 'Eswatini Seals';
  const academySubtitle = landingConfig?.hero?.academy_subtitle || 'Swim Academy';
  const academyLogo = landingConfig?.hero?.logo_url || certificate.academy_logo_url;
  
  // ONLY use landing_config signature - NO FALLBACK
  const digitalSignature = landingConfig?.certificate?.signature_url;
  
  // Build full signature URL for rendering
  const signatureUrl = digitalSignature 
    ? (digitalSignature.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${digitalSignature}` : digitalSignature)
    : null;

  // If signature is missing, show error (required for certificate generation)
  if (!signatureUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center p-8" data-testid="certificate-error">
          <p className="text-red-500 text-lg font-semibold">Certificate Generation Failed</p>
          <p className="text-slate-500 mt-2">Digital signature not configured. Please upload a signature in Settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-white flex items-center justify-center ${isPrint ? 'p-0' : 'p-8'}`}>
      <div 
        ref={contentRef}
        id="certificate-content" 
        data-ready={imagesLoaded ? 'true' : 'false'}
        className="w-[794px] bg-white"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        {/* Certificate Card */}
        <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-teal-100">
          {/* Tier Banner */}
          <div className={`${tier.bg} text-white py-4 px-6 flex items-center justify-center gap-3`}>
            <span className="text-3xl">{tier.emoji}</span>
            <span className="font-bold text-xl uppercase tracking-wider">{tier.text} CERTIFICATE</span>
          </div>

          <div className="p-10">
            {/* Academy Header with Logo */}
            <div className="text-center mb-8">
              <div className="flex justify-center items-center gap-4 mb-4">
                {academyLogo ? (
                  <img 
                    src={academyLogo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${academyLogo}` : academyLogo} 
                    alt="Academy Logo" 
                    className="w-24 h-24 rounded-full object-cover shadow-lg"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-24 h-24 rounded-full bg-teal-100 items-center justify-center shadow-lg ${academyLogo ? 'hidden' : 'flex'}`}>
                  <Award className="w-14 h-14 text-teal-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-teal-700">
                {academyName} {academySubtitle}
              </h1>
              <p className="text-slate-500 text-base mt-1">Drowning Prevention & Water Safety</p>
              
              {certificate.school_name && (
                <p className="text-slate-600 text-sm mt-2">
                  In collaboration with <strong>{certificate.school_name}</strong>
                </p>
              )}
            </div>

            {/* Certificate Title */}
            <div className="text-center space-y-4 mb-8">
              <p className="text-slate-500 uppercase tracking-widest text-sm font-semibold">
                Certificate of Completion
              </p>
              <div className="py-6 border-y-2 border-dashed border-slate-200">
                <p className="text-slate-500 text-base">This is to certify that</p>
                <p className="text-4xl font-bold text-slate-900 my-4">{certificate.student_name}</p>
                <p className="text-slate-500 text-base">has successfully completed the</p>
                <p className="text-2xl font-semibold text-teal-600 mt-3">{certificate.program_name}</p>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-4 gap-4 text-center mb-8">
              <div className="p-4 rounded-xl bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Instructor</p>
                <p className="font-semibold text-sm text-slate-700">{certificate.coach_name || 'Academy Coach'}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Location</p>
                <p className="font-semibold text-sm text-slate-700">{certificate.location_name || 'Academy Pool'}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Issue Date</p>
                <p className="font-semibold text-sm text-slate-700">
                  {certificate.completion_date || new Date().toISOString().split('T')[0]}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Lessons</p>
                <p className="font-semibold text-sm text-slate-700">{certificate.total_lessons || 10}</p>
              </div>
            </div>

            {/* Program Period */}
            {certificate.program_start_date && certificate.program_end_date && (
              <div className="text-center mb-8 py-3 px-6 bg-teal-50 rounded-xl inline-block mx-auto w-full">
                <p className="text-teal-700 text-sm">
                  Program Period: {certificate.program_start_date} — {certificate.program_end_date}
                </p>
              </div>
            )}

            {/* QR Code & Digital Signature */}
            <div className="flex items-end justify-between pt-6 border-t-2 border-dashed border-slate-200">
              <div className="text-center">
                {qrCodeUrl && (
                  <img src={qrCodeUrl} alt="QR Code" className="w-24 h-24 mb-2" />
                )}
                <p className="text-xs text-slate-400">Scan to Verify</p>
              </div>
              
              <div className="text-center flex-1 mx-8">
                <p className="text-xs text-slate-400 font-mono">
                  ID: {certificateId.substring(0, 8).toUpperCase()}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Approved by: {certificate.approved_by || 'Administrator'}
                </p>
              </div>
              
              <div className="text-right">
                <img 
                  src={signatureUrl}
                  alt="Digital Signature"
                  className="w-36 h-16 object-contain mb-2"
                  data-testid="certificate-signature"
                />
                <p className="text-xs text-slate-500">Digital Signature</p>
                <p className="text-xs text-slate-400 italic">{academyName} {academySubtitle}</p>
              </div>
            </div>
          </div>

          {/* Watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
            <Award className="w-72 h-72 text-teal-900" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatePrint;
